/**
 * Project scope — pure resolution, filter and sort (card 1.1).
 *
 * The active scope is `'all'` | `'none'` | a project id. A project id only
 * filters once it RESOLVES to a project the user can still see:
 *
 *   status 'ready'    the project is known (personal, or a team on the roster)
 *   status 'pending'  not known yet — projects not loaded, or the team roster
 *                     is unavailable. The id is KEPT (it may resolve in a
 *                     moment) and the filter matches NOTHING: a missing roster
 *                     must never turn into "show everything of that team".
 *   status 'gone'     known to be unreachable — the project was deleted, or
 *                     its team is not on a loaded roster (the user left).
 *                     The caller falls back to 'all'.
 *
 * Items are matched either by a stamped `projectId` (boards, documents) or by
 * a graph node id the project `contains` (connections: `ssh_host:<vaultId>`,
 * `db_connection:<vaultId>` — never a field in the vault).
 */
export const ALL_PROJECTS = 'all';
export const NO_PROJECT = 'none';

/** Anything that is not a non-empty id or a known keyword means "all". */
export function normalizeScopeId(value) {
  if (value === NO_PROJECT) return NO_PROJECT;
  const id = value == null ? '' : String(value).trim();
  return id && id !== ALL_PROJECTS ? id : ALL_PROJECTS;
}

const teamOf = (project) => {
  const key = String(project?.scopeKey || '');
  return key.startsWith('team:') ? key.slice('team:'.length) : null;
};

/**
 * @param activeProjectId  the stored id
 * @param known  { loaded, projects: [{ id, scopeKey, … }], rosterKnown, rosterTeamIds: string[] }
 * @returns {{ activeProjectId, activeProject, status: 'ready'|'pending'|'gone' }}
 */
export function resolveActiveProject(activeProjectId, known = {}) {
  const id = normalizeScopeId(activeProjectId);
  if (id === ALL_PROJECTS || id === NO_PROJECT) return { activeProjectId: id, activeProject: null, status: 'ready' };
  if (!known.loaded) return { activeProjectId: id, activeProject: null, status: 'pending' };
  const roster = new Set((known.rosterTeamIds || []).map(String));
  const project = (known.projects || []).find((p) => p && String(p.id) === id) || null;
  if (project) {
    const team = teamOf(project);
    if (!team || roster.has(team)) return { activeProjectId: id, activeProject: project, status: 'ready' };
    return { activeProjectId: id, activeProject: null, status: known.rosterKnown ? 'gone' : 'pending' };
  }
  // Not found: with the roster unknown it may be a team project not loaded yet.
  return { activeProjectId: id, activeProject: null, status: known.rosterKnown ? 'gone' : 'pending' };
}

/**
 * Is `item` in the active scope?
 * @param item   { projectId?, nodeId? }
 * @param scope  { activeProjectId, activeProject, memberIds?: Set<nodeId>, assignedIds?: Set<nodeId> }
 *               memberIds = what the active project contains; assignedIds = what ANY project contains
 */
export function itemInProject(item, scope = {}) {
  const id = normalizeScopeId(scope.activeProjectId);
  if (id === ALL_PROJECTS) return true;
  if (!item) return false;
  const stamped = item.projectId == null || item.projectId === '' ? null : String(item.projectId);
  if (id === NO_PROJECT) return !stamped && !(item.nodeId && scope.assignedIds?.has(item.nodeId));
  if (!scope.activeProject || String(scope.activeProject.id) !== id) return false; // pending / gone: nothing
  if (stamped === id) return true;
  return Boolean(item.nodeId && scope.memberIds?.has(item.nodeId));
}

/** `{ inProject, others }`, each in the input order. Under "All projects" everything is in `others`. */
export function splitByProject(rows, scope = {}, toItem = (row) => row) {
  const inProject = [];
  const others = [];
  const all = normalizeScopeId(scope.activeProjectId) === ALL_PROJECTS;
  for (const row of rows || []) {
    if (!all && itemInProject(toItem(row), scope)) inProject.push(row);
    else others.push(row);
  }
  return { inProject, others };
}

/** The active project's rows first, then the rest — stable, a new array, nothing dropped. */
export function sortProjectFirst(rows, scope = {}, toItem) {
  const { inProject, others } = splitByProject(rows, scope, toItem);
  return [...inProject, ...others];
}
